import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi, widgetsApi } from '../lib/api-client';
import { useAuth } from '../contexts/AuthContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Calendar,
  TrendingUp,
  MessageSquare,
  Users,
  Phone,
  FileText,
  Target,
  Activity,
  ThumbsUp,
  ThumbsDown,
  Minus,
} from 'lucide-react';

export default function Analytics() {
  const { organization } = useAuth();
  const [selectedWidget, setSelectedWidget] = useState<string>('all');
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  const { data: widgetsData } = useQuery({
    queryKey: ['widgets', organization?.id],
    queryFn: () => widgetsApi.list(organization!.id),
    enabled: !!organization,
  });

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics', organization?.id, selectedWidget, dateRange],
    queryFn: () => {
      const endDate = Date.now();
      const startDate = endDate - (dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90) * 24 * 60 * 60 * 1000;

      return selectedWidget === 'all'
        ? analyticsApi.getOrganizationAnalytics(organization!.id, { startDate, endDate })
        : analyticsApi.getWidgetAnalytics(selectedWidget, { startDate, endDate });
    },
    enabled: !!organization,
  });

  const widgets = widgetsData?.data?.data || [];
  const analytics = analyticsData?.data?.data || {};

  // Mock data for charts (in production, this would come from the analytics API)
  const messagesTrendData = [
    { date: '2024-10-27', messages: 45 },
    { date: '2024-10-28', messages: 52 },
    { date: '2024-10-29', messages: 48 },
    { date: '2024-10-30', messages: 61 },
    { date: '2024-10-31', messages: 55 },
    { date: '2024-11-01', messages: 67 },
    { date: '2024-11-02', messages: 73 },
  ];

  const conversationsByWidgetData = widgets.slice(0, 5).map((widget: any) => ({
    name: widget.name,
    conversations: Math.floor(Math.random() * 100),
  }));

  const stats = [
    {
      label: 'Total Messages',
      value: analytics.totalMessages || 0,
      change: '+12.5%',
      icon: MessageSquare,
      color: 'bg-blue-500',
    },
    {
      label: 'Active Conversations',
      value: analytics.activeConversations || 0,
      change: '+8.2%',
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      label: 'Unique Visitors',
      value: analytics.uniqueVisitors || 0,
      change: '+15.3%',
      icon: Users,
      color: 'bg-purple-500',
    },
    {
      label: 'Avg Response Time',
      value: `${analytics.avgResponseTime || 0}s`,
      change: '-5.1%',
      icon: Calendar,
      color: 'bg-orange-500',
    },
  ];

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500 mt-2">Track performance and engagement metrics</p>
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={selectedWidget}
            onChange={(e) => setSelectedWidget(e.target.value)}
            className="input"
          >
            <option value="all">All Widgets</option>
            {widgets.map((widget: any) => (
              <option key={widget.id} value={widget.id}>
                {widget.name}
              </option>
            ))}
          </select>

          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="input"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <div key={stat.label} className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <span className={`text-sm font-medium ${
                stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'
              }`}>
                {stat.change}
              </span>
            </div>
            <p className="text-sm text-gray-600">{stat.label}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Messages Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={messagesTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="messages"
                stroke="#3B82F6"
                strokeWidth={2}
                dot={{ fill: '#3B82F6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Conversations by Widget</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={conversationsByWidgetData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="conversations" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Avg Messages per Conversation</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.avgMessagesPerConv || 0}</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Conversion Rate</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.conversionRate || 0}%</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Customer Satisfaction</p>
            <p className="text-3xl font-bold text-gray-900">{analytics.satisfaction || 0}/5</p>
          </div>
        </div>
      </div>

      {/* Phone Call Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Phone Call Metrics</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Calls</span>
              <span className="text-2xl font-bold text-gray-900">247</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Avg Call Duration</span>
              <span className="text-2xl font-bold text-gray-900">3:42</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Answer Rate</span>
              <span className="text-2xl font-bold text-green-600">87%</span>
            </div>
          </div>
          <div className="mt-6">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: 'Mon', inbound: 12, outbound: 5 },
                { name: 'Tue', inbound: 15, outbound: 8 },
                { name: 'Wed', inbound: 18, outbound: 6 },
                { name: 'Thu', inbound: 14, outbound: 9 },
                { name: 'Fri', inbound: 20, outbound: 7 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="inbound" fill="#10B981" name="Inbound" />
                <Bar dataKey="outbound" fill="#3B82F6" name="Outbound" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Document Analytics</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Documents Processed</span>
              <span className="text-2xl font-bold text-gray-900">1,234</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Chunks</span>
              <span className="text-2xl font-bold text-gray-900">8,562</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Queries Answered</span>
              <span className="text-2xl font-bold text-purple-600">3,847</span>
            </div>
          </div>
          <div className="mt-6">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'PDF', value: 45 },
                    { name: 'TXT', value: 35 },
                    { name: 'DOCX', value: 20 },
                  ]}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  <Cell fill="#3B82F6" />
                  <Cell fill="#10B981" />
                  <Cell fill="#8B5CF6" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Sentiment Analysis */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-blue-600" />
          <h2 className="text-xl font-semibold text-gray-900">Sentiment Analysis</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <ThumbsUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-green-600">65%</p>
            <p className="text-sm text-gray-600 mt-1">Positive</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
            <Minus className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-600">25%</p>
            <p className="text-sm text-gray-600 mt-1">Neutral</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
            <ThumbsDown className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-red-600">10%</p>
            <p className="text-sm text-gray-600 mt-1">Negative</p>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={[
            { date: '10/27', positive: 62, neutral: 28, negative: 10 },
            { date: '10/28', positive: 65, neutral: 26, negative: 9 },
            { date: '10/29', positive: 63, neutral: 27, negative: 10 },
            { date: '10/30', positive: 68, neutral: 23, negative: 9 },
            { date: '10/31', positive: 64, neutral: 26, negative: 10 },
            { date: '11/01', positive: 66, neutral: 24, negative: 10 },
            { date: '11/02', positive: 65, neutral: 25, negative: 10 },
          ]}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="positive" stroke="#10B981" strokeWidth={2} />
            <Line type="monotone" dataKey="neutral" stroke="#6B7280" strokeWidth={2} />
            <Line type="monotone" dataKey="negative" stroke="#EF4444" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Lead Scoring */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Lead Scoring</h2>
          </div>
          <span className="text-sm text-gray-500">Top leads this month</span>
        </div>
        <div className="space-y-3">
          {[
            { name: 'John Doe', email: 'john@example.com', score: 92, interactions: 15, lastContact: '2024-11-02' },
            { name: 'Jane Smith', email: 'jane@example.com', score: 85, interactions: 12, lastContact: '2024-11-01' },
            { name: 'Bob Johnson', email: 'bob@example.com', score: 78, interactions: 10, lastContact: '2024-11-02' },
            { name: 'Alice Williams', email: 'alice@example.com', score: 72, interactions: 9, lastContact: '2024-10-31' },
            { name: 'Charlie Brown', email: 'charlie@example.com', score: 68, interactions: 8, lastContact: '2024-11-01' },
          ].map((lead, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
              <div className="flex-1">
                <div className="font-medium text-gray-900">{lead.name}</div>
                <div className="text-sm text-gray-500">{lead.email}</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-700">{lead.interactions} interactions</div>
                  <div className="text-xs text-gray-500">Last: {new Date(lead.lastContact).toLocaleDateString()}</div>
                </div>
                <div
                  className={`px-3 py-1 rounded-full text-sm font-bold ${
                    lead.score >= 80
                      ? 'bg-green-100 text-green-700'
                      : lead.score >= 65
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {lead.score}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Conversion Funnel</h2>
        <div className="space-y-3">
          {[
            { name: 'Visitors', count: 10000, percentage: 100 },
            { name: 'Engaged', count: 6500, percentage: 65 },
            { name: 'Qualified', count: 3200, percentage: 32 },
            { name: 'Proposals Sent', count: 1800, percentage: 18 },
            { name: 'Converted', count: 950, percentage: 9.5 },
          ].map((stage, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{stage.name}</span>
                <span className="text-sm text-gray-600">
                  {stage.count.toLocaleString()} ({stage.percentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${stage.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
